import { Alert, Button, Card, DatePicker, Form, Input, Popconfirm, Select, Space, message } from 'antd';
import { useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import LmsStatusTag from '@/pages/education/components/LmsStatusTag';
import LmsUnitSelect from '@/pages/education/components/LmsUnitSelect';
import { EducationSectionSelect, EducationSubjectSelect } from '@/pages/education/components/EducationLookupSelects';
import { lmsErrorMessage, useLmsAccess } from '@/pages/education/utils/lmsAccess';

type Homework = {
  id: string; section_id: string; subject_id: string; lesson_plan_id?: string | null; curriculum_unit_id?: string | null;
  title: string; instructions?: string | null; due_on: string; status: string;
};
type PlanOption = { id: string; title: string; planned_on: string };
type Values = {
  section_id: string; subject_id: string; lesson_plan_id?: string; curriculum_unit_id?: string;
  title: string; instructions?: string; due_on: Dayjs;
};

export default function HomeworkFormPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canManage } = useLmsAccess();
  const [form] = Form.useForm<Values>();
  const sectionId = Form.useWatch('section_id', form);
  const subjectId = Form.useWatch('subject_id', form);

  const detail = useQuery({
    queryKey: ['education-lms-homework-edit', id],
    enabled: !isNew,
    queryFn: async () => (await api.get<{ assignment: Homework }>(`/education/lms/homework/${id}`)).data?.assignment,
  });
  const plans = useQuery({
    queryKey: ['education-lms-homework-plans', sectionId, subjectId],
    enabled: Boolean(sectionId),
    queryFn: async () => (await api.get<PlanOption[]>('/education/lms/lesson-plans', {
      params: { section_id: sectionId, subject_id: subjectId },
    })).data || [],
  });
  const hw = detail.data;
  useEffect(() => {
    if (!hw) return;
    form.setFieldsValue({
      section_id: hw.section_id,
      subject_id: hw.subject_id,
      lesson_plan_id: hw.lesson_plan_id || undefined,
      curriculum_unit_id: hw.curriculum_unit_id || undefined,
      title: hw.title,
      instructions: hw.instructions || undefined,
      due_on: dayjs(hw.due_on),
    });
  }, [hw, form]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['education-lms-homework'] });
    queryClient.invalidateQueries({ queryKey: ['education-lms-homework-detail', id] });
    queryClient.invalidateQueries({ queryKey: ['education-lms-homework-edit', id] });
    queryClient.invalidateQueries({ queryKey: ['education-lms-summary'] });
  };
  const save = useMutation({
    mutationFn: (values: Values) => {
      const payload = {
        ...values,
        due_on: values.due_on.format('YYYY-MM-DD'),
        lesson_plan_id: values.lesson_plan_id || null,
        curriculum_unit_id: values.curriculum_unit_id || null,
        instructions: values.instructions?.trim() || null,
      };
      return isNew
        ? api.post<Homework>('/education/lms/homework', payload)
        : api.put<Homework>(`/education/lms/homework/${id}`, {
          title: payload.title, due_on: payload.due_on, instructions: payload.instructions,
          lesson_plan_id: payload.lesson_plan_id, curriculum_unit_id: payload.curriculum_unit_id,
        });
    },
    onSuccess: (response) => {
      message.success(t('lmsSaved', { defaultValue: 'Saved' }));
      refresh();
      const savedId = (response as { data?: Homework }).data?.id || id;
      navigate(`/education/lms/homework/${savedId}`);
    },
    onError: (e) => message.error(lmsErrorMessage(e, 'Unable to save homework')),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/education/lms/homework/${id}`),
    onSuccess: () => { refresh(); navigate('/education/lms/homework'); },
    onError: (e) => message.error(lmsErrorMessage(e, 'Unable to delete homework')),
  });
  const closed = hw?.status === 'closed';

  return (
    <EducationPageChrome
      title={isNew ? t('lmsNewHomework', { defaultValue: 'New homework' }) : t('lmsEditHomework', { defaultValue: 'Edit homework' })}
      description={t('lmsHomeworkDraftHint', { defaultValue: 'Homework starts as a draft. Publish it from the homework page when ready.' })}
      tags={hw ? <LmsStatusTag status={hw.status} /> : undefined}
      extra={<Button onClick={() => navigate(isNew ? '/education/lms/homework' : `/education/lms/homework/${id}`)}>
        {t('lmsHomework', { defaultValue: 'Homework' })}
      </Button>}
    >
      {!canManage ? <Alert type="warning" showIcon message={t('lmsReadOnly', { defaultValue: 'Read-only access.' })} /> : null}
      <Card size="small" style={{ maxWidth: 820 }} loading={!isNew && detail.isLoading}>
        <Form<Values> form={form} layout="vertical" disabled={!canManage || closed} onFinish={(v) => save.mutate(v)}>
          <Form.Item name="section_id" label={t('section', { defaultValue: 'Section' })} rules={[{ required: true }]}>
            <EducationSectionSelect disabled={!isNew} />
          </Form.Item>
          <Form.Item name="subject_id" label={t('subject', { defaultValue: 'Subject' })} rules={[{ required: true }]}>
            <EducationSubjectSelect
              disabled={!isNew}
              onChange={() => form.setFieldsValue({ curriculum_unit_id: undefined, lesson_plan_id: undefined })}
            />
          </Form.Item>
          <Form.Item name="lesson_plan_id" label={t('lmsLessonPlan', { defaultValue: 'Lesson plan (optional)' })}>
            <Select
              allowClear showSearch optionFilterProp="label" loading={plans.isLoading}
              options={(plans.data || []).map((p) => ({ value: p.id, label: `${p.planned_on} — ${p.title}` }))}
            />
          </Form.Item>
          <Form.Item name="curriculum_unit_id" label={t('lmsCurriculumUnit', { defaultValue: 'Curriculum unit (optional)' })}>
            <LmsUnitSelect subjectId={subjectId} />
          </Form.Item>
          <Form.Item name="title" label={t('title', { defaultValue: 'Title' })} rules={[{ required: true, max: 250 }]}>
            <Input maxLength={250} />
          </Form.Item>
          <Form.Item name="due_on" label={t('lmsDueOn', { defaultValue: 'Due on' })} rules={[{ required: true }]}>
            <DatePicker />
          </Form.Item>
          <Form.Item name="instructions" label={t('lmsInstructions', { defaultValue: 'Instructions' })}>
            <Input.TextArea rows={7} maxLength={20000} showCount />
          </Form.Item>
          {canManage && !closed ? (
            <Space wrap>
              <Button type="primary" htmlType="submit" loading={save.isPending}>{t('save', { defaultValue: 'Save' })}</Button>
              {!isNew && hw?.status === 'draft' ? (
                <Popconfirm title={t('lmsDelete', { defaultValue: 'Delete' })} onConfirm={() => remove.mutate()}>
                  <Button danger>{t('lmsDelete', { defaultValue: 'Delete' })}</Button>
                </Popconfirm>
              ) : null}
              <Button onClick={() => navigate('/education/lms/homework')}>{t('cancel', { defaultValue: 'Cancel' })}</Button>
            </Space>
          ) : null}
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
